import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import useAuthStore from '../store/authStore'
import { consumePostLoginRedirect } from '../utils/authRedirect'

// Helper function to decode JWT
const decodeToken = (token) => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const decoded = JSON.parse(atob(parts[1]))
    return decoded
  } catch (error) {
    console.error('Error decoding token:', error)
    return null
  }
}

const GoogleAuthCallbackPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState(null)
  const setToken = useAuthStore((state) => state.setToken)
  const setUser = useAuthStore((state) => state.setUser)
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current) return
    handledRef.current = true

    const token = searchParams.get('token')
    const errorParam = searchParams.get('error')
    const reason = searchParams.get('reason')

    if (reason === 'NOT_WHITELISTED') {
      navigate('/access-denied', { replace: true })
      return
    }

    if (errorParam) {
      setError(decodeURIComponent(errorParam))
      setTimeout(() => {
        navigate('/login', { replace: true })
      }, 3000)
      return
    }

    if (token) {
      try {
        setToken(token)

        const decoded = decodeToken(token)
        if (decoded) {
          setUser({
            id: decoded.sub,
            email: decoded.email,
            name: decoded.name || decoded.email,
            isAdmin: decoded.isAdmin,
          })
        }

        navigate(consumePostLoginRedirect(), { replace: true })
      } catch (err) {
        console.error('Auth error:', err)
        setError('Failed to process authentication. Please try again.')
        setTimeout(() => {
          navigate('/login', { replace: true })
        }, 3000)
      }
    } else {
      setError('No token received from authentication server.')
      setTimeout(() => {
        navigate('/login', { replace: true })
      }, 3000)
    }
  }, [searchParams, navigate, setToken, setUser])

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      {error ? (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            {error}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Redirecting to login...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#fff', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Completing authentication...
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default GoogleAuthCallbackPage
